import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { find, values } from "lodash";
import { stringifyUrl } from "query-string";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { useHotspots } from ".";
import AlertMessage from "../../components/AlertMessage";
import CenteredLoading from "../../components/CenteredLoading";
import Checkbox from "../../components/forms/Checkbox";
import DropdownSearch from "../../components/forms/DropdownSearch";
import FormProvider from "../../components/forms/FormProvider";
import HandleFieldChange from "../../components/forms/HandleFieldChange";
import UpgradeHotspotsModal from "../../components/modals/UpgradeHotspotsModal";
import { avatarsApi } from "../../utils/constants";
import { formatHotspotName } from "../../utils/hotspotUtils";
import { h3ToCenterChildMemoized } from "../../utils/memoizedMethods";
import { EtlHotspot, PaymentStep, PlanType } from "../../utils/types";
import useMapCenter from "../../utils/useMapCenter";
import { useBillingPlans } from "../billingPlans";
import { useLoading } from "../loading";
import { LoadingKey } from "../loading/LoadingProvider";
import { ModalConfig } from "../toast/ToastProvider";
import { useWallets } from "../wallets";
import { useWorkspaces } from "../workspaces";
import {
  convertEtlHotspotSearchResponse,
  HotspotsSearchResponse
} from "./HotspotsProvider";

interface Props {
  open: boolean;
  toggle: () => void;
  hotspot?: EtlHotspot;
}

const AddHotspotSidebar: React.FC<Props> = ({ open, toggle, hotspot }) => {
  const {
    workspaceHotspots,
    addWorkspaceHotspot,
    fetchWalletHotspotCount,
    walletHotspotCountMap,
    hotspotsLimitExceeded
  } = useHotspots();
  const { currentPlan, setPaymentStep } = useBillingPlans();
  const { currentWorkspace } = useWorkspaces();
  const { getIsReady } = useLoading();
  const { wallets, addWallet } = useWallets();
  const { setNewPlan } = useBillingPlans();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const centerInH3HexRes4 = useMapCenter((state) => state.centerInH3HexRes4);
  const [currentOwnerExceededLimit, setCurrentOwnerExceededLimit] = useState(0);
  const [upgradeHotspotsConfig, setUpgradeHotspotsConfig] =
    useState<ModalConfig>({
      open: false
    });
  const history = useHistory();

  const initialValues = useMemo(() => {
    return {
      hotspot: hotspot ?? "",
      addHotspotWallet: true
    };
  }, [hotspot]);

  const handleCloseSidebar = useCallback(() => {
    toggle();
    setCurrentOwnerExceededLimit(0);
  }, [toggle]);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      let success = false;

      if (
        (walletHotspotCountMap[formValues.hotspot.owner] || 0) +
          (currentWorkspace?.counts.hotspot || 0) >
          Number(currentWorkspace?.active_plan?.allowed_hotspots) &&
        formValues.addHotspotWallet
      ) {
        setCurrentOwnerExceededLimit(
          walletHotspotCountMap[formValues.hotspot.owner]
        );

        return;
      }

      setCurrentOwnerExceededLimit(0);

      if (formValues.addHotspotWallet) {
        const apiResponse = await addWallet(formValues.hotspot.owner);
        success = apiResponse?.status || false;
      } else {
        const apiResponse = await addWorkspaceHotspot(formValues.hotspot.id);

        if (apiResponse?.status) {
          success = true;
        }

        if (!apiResponse?.status) {
          setErrorMessage(apiResponse?.message || "Something went wrong");

          return;
        }
      }

      if (success) {
        handleCloseSidebar();
      } else {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [
      addWallet,
      addWorkspaceHotspot,
      currentWorkspace?.active_plan?.allowed_hotspots,
      currentWorkspace?.counts.hotspot,
      handleCloseSidebar,
      walletHotspotCountMap
    ]
  );

  const fetchHotspots = useCallback(
    async (searchInput: string | undefined) => {
      const findHotspotsUrl = stringifyUrl({
        url: `${process.env.REACT_APP_ETL_API}/hotspots/search-lean`,
        query: {
          name: searchInput || "",
          limit: 5,
          ...(centerInH3HexRes4
            ? {
                proximity_hex: h3ToCenterChildMemoized(centerInH3HexRes4, 12)
              }
            : {})
        }
      });

      return fetch(findHotspotsUrl)
        .then((res) => res.json())
        .then(
          (res: HotspotsSearchResponse) =>
            convertEtlHotspotSearchResponse(res.data) || []
        );
    },
    [centerInH3HexRes4]
  );

  useEffect(() => {
    if (hotspot?.owner) {
      fetchWalletHotspotCount(hotspot.owner);
    }
  }, [fetchWalletHotspotCount, hotspot?.owner]);

  const validateHotspotField = useCallback(
    async (hotspot: EtlHotspot | undefined, addHotspotWallet: boolean) => {
      if (!hotspot) {
        return "Required";
      }

      if (!walletHotspotCountMap[hotspot.owner]) {
        fetchWalletHotspotCount(hotspot.owner);
      }

      const walletIsAlreadyImported = !!find(values(wallets), {
        address: hotspot.owner
      });

      if (walletIsAlreadyImported && addHotspotWallet) {
        return "This hotspot's wallet was already added to your workspace, please uncheck the 'Add hotspot wallet' checkbox";
      }

      const hotspotIsAlreadyTracked = !!find(values(workspaceHotspots), {
        id: hotspot.id
      });

      if (hotspotIsAlreadyTracked) {
        return "This hotspot was already added to your workspace.";
      }
    },
    [fetchWalletHotspotCount, walletHotspotCountMap, wallets, workspaceHotspots]
  );

  const trackWalletHotspotsLabel = useCallback(
    (walletAddress: string | undefined) => {
      const walletHotspotsCount =
        walletHotspotCountMap[walletAddress || ""] || 0;

      switch (walletHotspotsCount) {
        case 0:
          return "Add hotspot wallet";
        case 1:
          return "Add hotspot wallet (incl. this hotspot)";
        default:
          return `Add hotspot wallet (incl. all ${walletHotspotsCount} hotspots)`;
      }
    },
    [walletHotspotCountMap]
  );

  const limitExceededAlertMessage = useMemo(() => {
    if (
      (!hotspotsLimitExceeded && currentOwnerExceededLimit === 0) ||
      !currentWorkspace
    ) {
      return null;
    }

    const { allowed_hotspots } = currentWorkspace.active_plan;

    return (
      <AlertMessage
        type="warning"
        title={
          <span>
            This workspace has the {currentPlan?.name} plan which is limited to{" "}
            {allowed_hotspots} hotspots. You are trying to add a new hotspot,
            which is not allowed. Please{" "}
            <b
              className="font-bold dark:text-hotspotty-300 hover:underline cursor-pointer"
              onClick={() => {
                if (currentPlan?.type !== PlanType.community) {
                  setUpgradeHotspotsConfig({ open: true });
                  setNewPlan((prevState) => ({
                    ...prevState,
                    hotspotAmount: allowed_hotspots + currentOwnerExceededLimit
                  }));

                  return;
                }

                history.push("/pricing");
                handleCloseSidebar();
                setPaymentStep(PaymentStep.plans);
              }}
            >
              increase your hotspot limit
            </b>{" "}
            or remove hotspots you no longer need.
          </span>
        }
      />
    );
  }, [
    hotspotsLimitExceeded,
    currentOwnerExceededLimit,
    currentWorkspace,
    currentPlan?.name,
    currentPlan?.type,
    history,
    handleCloseSidebar,
    setPaymentStep,
    setNewPlan
  ]);

  const isLoading = useMemo(
    () => !getIsReady([LoadingKey.workspaceWalletHotspotCount]),
    [getIsReady]
  );

  const checkCanshow = useCallback(() => {
    if (open || upgradeHotspotsConfig.open) return true;

    return false;
  }, [open, upgradeHotspotsConfig.open]);

  return (
    <>
      <UpgradeHotspotsModal
        config={upgradeHotspotsConfig}
        setConfig={setUpgradeHotspotsConfig}
      />

      <Transition.Root show={checkCanshow()} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed inset-0 z-20 overflow-hidden"
          open={checkCanshow()}
          onClose={() => {
            if (upgradeHotspotsConfig.open) return;

            handleCloseSidebar();
          }}
        >
          <div className="absolute inset-0 overflow-hidden">
            <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex">
              <div
                className="absolute inset-0"
                onClick={() => {
                  if (upgradeHotspotsConfig.open) return;

                  handleCloseSidebar();
                }}
              />
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <div className="relative w-screen max-w-md h-full flex flex-col">
                  <div className="flex-shrink-0 py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                      <Dialog.Title className="text-lg font-medium text-white">
                        Add hotspot
                      </Dialog.Title>
                      <div className="ml-3 h-7 flex items-center">
                        <button
                          type="button"
                          className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                          onClick={handleCloseSidebar}
                        >
                          <span className="sr-only">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <FormProvider
                    classnames={{ wrapper: "flex-1" }}
                    onCancel={handleCloseSidebar}
                    disabled={isLoading || limitExceededAlertMessage !== null}
                    error={errorMessage}
                    formikProps={{
                      onSubmit,
                      initialValues
                    }}
                    orderedFieldNames={ORDERED_FIELD_NAMES}
                  >
                    {({ isSubmitting, values }: FormikProps<FormValues>) => {
                      return (
                        <div className="flex-1 h-0 overflow-y-auto">
                          <div className="flex-1 flex flex-col justify-between">
                            <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                              <div className="space-y-6 pt-6 pb-5">
                                {limitExceededAlertMessage}

                                <Field
                                  label="Hotspot"
                                  name="hotspot"
                                  placeholder="Search a hotspot"
                                  disabled={
                                    isSubmitting ||
                                    limitExceededAlertMessage !== null
                                  }
                                  getImageUrl={(hotspot: EtlHotspot) =>
                                    stringifyUrl({
                                      url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                      query: { w: 24, h: 24 }
                                    })
                                  }
                                  fetchItems={fetchHotspots}
                                  itemToString={formatHotspotName}
                                  component={DropdownSearch}
                                  validate={(hotspot: EtlHotspot | undefined) =>
                                    validateHotspotField(
                                      hotspot,
                                      values.addHotspotWallet
                                    )
                                  }
                                />

                                {!isLoading ? (
                                  <Field
                                    label={trackWalletHotspotsLabel(
                                      values.hotspot?.owner
                                    )}
                                    name="addHotspotWallet"
                                    disabled={isSubmitting}
                                    type="checkbox"
                                    component={Checkbox}
                                  />
                                ) : (
                                  <span className="flex items-center text-sm pt-0.5 font-medium leading-5 text-gray-900 dark:text-white">
                                    <CenteredLoading className="h-5 w-5 mr-4" />
                                    Checking wallet hotspots
                                  </span>
                                )}

                                <HandleFieldChange<FormValues>
                                  name="addHotspotWallet"
                                  onChange={() => {
                                    if (!values.addHotspotWallet) {
                                      setCurrentOwnerExceededLimit(0);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  </FormProvider>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default AddHotspotSidebar;

//
// Utils
//

interface FormValues {
  hotspot: EtlHotspot;
  addHotspotWallet: boolean;
}

const ORDERED_FIELD_NAMES = ["hotspot", "addHotspotWallet"];
